import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { Marker } from '@dreamstack/icons/src/icon-components/Marker'
import { Share } from '@dreamstack/icons/src/icon-components/Share'
import { SimpleDefinitionList } from '@dreamstack/simple-components/src/lib/SimpleDefinitionList'
import { PropertyStatus } from '@dreamstack/real-estate-logic'
import type { FullProject } from '@dreamstack/web/lib/prisma/projects'
import type { FullProperty } from '@dreamstack/web/lib/prisma/properties'
import CloseIcon from '@mui/icons-material/Close'
import first from 'lodash/first'
import minBy from 'lodash/minBy'
import some from 'lodash/some'
import React, { type FunctionComponent, useEffect, useState } from 'react'
// cn utility from util package
import { cn } from '@dreamstack/util'
import { SocialShare } from '../favorites/SocialShare'
import { useNotificationOptions } from '../hooks/useNotificationOptions'
import { useSimpleNotificationPopover } from '../hooks/useSimpleNotificationPopover'
import { PropAndProjectContactForm } from '../contactForm/PropAndProjectContactForm'
import { ShareContactWrapper } from '../contactForm/ShareContactWrapper'
import { usePropertyPriceVisibility } from '../hooks/usePropertyPriceVisibility'
import { GridWithGapsNoCols } from '../layout/MainGrid'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import {
  Css_Text_16_24_tw,
  Css_Text_20_30_To_16_24_tw,
  HeadingH4,
  PropertyHeadingH3,
  PropertyPriceText,
  PropertyRoomAndSqmText,
  PropertySmallText,
} from '../layout/Texts'
import { useGetPropertyUrl } from './routing/useGetPropertyUrl'
import { useWebShareAuto } from '../webshare/useWebShareAuto'
import { MyLikeButton } from '../my/MyLikeButton'
import { PropertyContactButton } from './PropertyContactButton'
import { PropertyInformationList } from './PropertyInformationList'
import { StyledBadge } from './StatusBadge'
import { scrollToMap } from './map/scrollToMap'

const StyledEur: FunctionComponent<React.PropsWithChildren<{}>> = ({
  children,
}) => <span className={cn('font-semibold', Css_Text_16_24_tw)}>{children}</span>

type PriceWrapperProps = React.PropsWithChildren<{
  canShare: boolean
  className?: string
}>
const PriceWrapper: FunctionComponent<PriceWrapperProps> = ({
  canShare,
  className,
  children,
}) => (
  <div
    className={cn(
      'relative',
      canShare ? 'mt-px-24' : 'mt-0 md:mt-px-24',
      className
    )}
  >
    {children}
  </div>
)

type ButtonContainerProps = React.PropsWithChildren<
  { isRow: boolean; className?: string } & React.HTMLAttributes<HTMLDivElement>
>
const ButtonContainer = React.forwardRef<HTMLDivElement, ButtonContainerProps>(
  ({ isRow, className, children, ...rest }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex', isRow ? 'flex-row' : 'flex-col', className)}
        {...rest}
      >
        {children}
      </div>
    )
  }
)
ButtonContainer.displayName = 'ButtonContainer'

const ButtonWrapper: FunctionComponent<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div className={cn('flex-row flex mt-px-32 md:my-0', className)}>
    {children}
  </div>
)

const MapButtonText: FunctionComponent<
  React.PropsWithChildren<{ onClick?: () => void }>
> = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'ml-px-12 bg-transparent p-0 inline-block text-left',
      Css_Text_20_30_To_16_24_tw
    )}
  >
    {children}
  </button>
)

export type PropertyInformationProps = React.PropsWithChildren<{
  properties?: FullProperty[]
  property?: FullProperty
  project?: FullProject
  statusLabel?: string
  isOnDarkBg?: boolean
}>

export const PropertyInformation: FunctionComponent<
  PropertyInformationProps
> = ({ properties = [], property, statusLabel }) => {
  // console.log(property)
  const formatCurrency = useFormatToCurrencyCutoff()
  const t = useTranslation()
  const formatDecimal = useFormatToDecimal()
  const propertyPriceVisibility = usePropertyPriceVisibility()
  const { canShare, share } = useWebShareAuto()
  const [openContactForm, setOpenContactForm] = useState<boolean>(false)
  const { offset, placement } = useNotificationOptions(
    'SocialSharingPropertyInformation'
  )

  // fix begin
  // description https://github.com/vercel/next.js/discussions/17443
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  // fix end

  // TODO: Refactor.
  const safeProperty =
    property || (minBy(properties, (p) => p?.price) ?? first(properties))!

  const hasPrice = !(safeProperty?.price == null)

  const price = propertyPriceVisibility(safeProperty)
    ? formatCurrency(safeProperty.price!)
    : '???.???'

  const price_per_sqm =
    safeProperty?.price_per_sqm && propertyPriceVisibility(safeProperty)
      ? formatDecimal(safeProperty.price_per_sqm)
      : '???'

  const rented = safeProperty?.rented
  const formattedRented = hasPrice
    ? rented
      ? t(`accentro:yes-no-boolean-${rented}`)
      : '-'
    : '???'
  const formattedRentalIncome = hasPrice
    ? !(property?.rental_income == null)
      ? formatCurrency(property?.rental_income) + ' '
      : '-'
    : '??? '

  const formattedRentalSubsidary = hasPrice
    ? !(property?.rent_subsidy == null)
      ? formatCurrency(property?.rent_subsidy) + ' '
      : '-'
    : '??? '

  const projectHasGeoLocation = some(properties, (p) => !!p.geoLocation)

  const getPropertyUrl = useGetPropertyUrl()

  const propertyUrl = getPropertyUrl({
    city: safeProperty?.city ?? undefined,
    id: safeProperty.id,
  })

  const {
    NotificationPopoverComponent: SocialShareComponent,
    openNotification: openSocialShare,
    setReferenceElement: setSocialShareElement,
    notificationPopoverProps: socialPopoverProps,
  } = useSimpleNotificationPopover({
    options: { placement: placement },
    offset: offset,
  })

  const showGotoMapButton = !!property?.geoLocation || projectHasGeoLocation

  let actualStatusLabel = statusLabel
  if (!statusLabel && !hasPrice) {
    actualStatusLabel = t('accentro:notAvailable')
  }

  return (
    <>
      <div className={cn('mt-px-40 col-span-12 lg:col-span-8')}>
        <div className={cn('flex justify-between')}>
          <div className={cn('flex flex-col flex-wrap')}>
            <PropertyHeadingH3>
              <div>
                {/* TODO: What to display if there is no property in project? */}
                {safeProperty?.street + ' ' + safeProperty?.house_number}
              </div>
              <div>{safeProperty?.zip_code + ' ' + safeProperty?.city}</div>
            </PropertyHeadingH3>
            {showGotoMapButton && (
              <div
                className={cn(
                  'text-accentroAqua-full font-semibold py-px-4 mt-px-16 md:mt-px-20 lg:mt-px-24 cursor-pointer'
                )}
              >
                <div className={cn('flex justify-start items-center')}>
                  <Marker className={cn('h-px-32 w-px-32')} />
                  <MapButtonText onClick={scrollToMap}>
                    {t('accentro:showOnMap')}
                  </MapButtonText>
                </div>
              </div>
            )}
          </div>
          <div className={cn('relative md:mr-px-128 ml:mr-px-64')}>
            {statusLabel && property?.status !== PropertyStatus.NOT_AVAILABLE && (
              <StyledBadge status={property?.status ?? 0}>
                <PropertyRoomAndSqmText className={cn('p-px-8 text-center')}>
                  {statusLabel}
                </PropertyRoomAndSqmText>
              </StyledBadge>
            )}
          </div>
        </div>
        <PropertyInformationList property={property} />
        <GridWithGapsNoCols
          className={cn('grid grid-cols-5 md:grid-cols-7 mt-px-32 md:mt-px-40')}
        >
          <div
            className={cn(
              'col-span-5 grid grid-cols-2 gap-x-px-32 gap-y-px-24 sm:flex sm:space-x-px-32 sm:gap-x-px-16'
            )}
          >
            <SimpleDefinitionList
              className={cn('col-start-1')}
              big
              dt={
                <PropertyRoomAndSqmText
                  className={cn(
                    'relative',
                    !hasPrice && 'blur-[12px] select-none pointer-events-none'
                  )}
                >
                  {formattedRented}
                </PropertyRoomAndSqmText>
              }
              dd={<PropertySmallText>{t('accentro:rented')}</PropertySmallText>}
            />
            <SimpleDefinitionList
              className={cn('col-start-1')}
              big
              dt={
                <PropertyRoomAndSqmText
                  className={cn(
                    'relative',
                    !hasPrice && 'blur-[12px] select-none pointer-events-none'
                  )}
                >
                  {formattedRentalIncome}
                  {formattedRentalIncome !== '-' && <StyledEur>EUR</StyledEur>}
                </PropertyRoomAndSqmText>
              }
              dd={
                <PropertySmallText>
                  {t('accentro:rentalRevenue')}
                </PropertySmallText>
              }
            />
            <SimpleDefinitionList
              big
              dt={
                <PropertyRoomAndSqmText
                  className={cn(
                    'relative',
                    !hasPrice && 'blur-[12px] select-none pointer-events-none'
                  )}
                >
                  {formattedRentalSubsidary}
                  {formattedRentalSubsidary !== '-' && (
                    <StyledEur>EUR</StyledEur>
                  )}
                </PropertyRoomAndSqmText>
              }
              dd={
                <PropertySmallText>
                  {t('accentro:property.rentalSubsidary')}
                </PropertySmallText>
              }
            />
          </div>
        </GridWithGapsNoCols>
      </div>
      <div
        className={cn(
          'col-span-12 md:mt-px-24 md:flex md:justify-between lg:block lg:mt-px-80 lg:col-start-9 lg:col-span-4'
        )}
      >
        <PriceWrapper
          className={cn(
            !propertyPriceVisibility(safeProperty) &&
              'blur-[12px] select-none pointer-events-none'
          )}
          canShare={mounted && canShare}
        >
          <PropertyPriceText>
            {price} <StyledEur>EUR</StyledEur>
          </PropertyPriceText>
          <PropertySmallText className={cn('font-normal pb-px-8 md:pb-px-16')}>
            {price_per_sqm} / {t('accentro:squareMeter')}
          </PropertySmallText>
          <PropertySmallText className={cn('font-normal')}>
            {t('accentro:freeOfCommission')}
          </PropertySmallText>
        </PriceWrapper>
        <ShareContactWrapper canShare={mounted && canShare} isProperty={true}>
          <ButtonWrapper>
            <MyLikeButton
              property={safeProperty}
              propertyId={safeProperty.id}
            />
            {mounted && (
              <ButtonContainer
                className={cn(
                  'ml-px-24 mr-0 md:order-first md:mr-px-24 md:ml-0 cursor-pointer'
                )}
                ref={setSocialShareElement}
                isRow={true}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openSocialShare()
                  }
                }}
                onClick={() => {
                  openSocialShare()
                }}
                aria-label={t('accentro:share')}
              >
                <SocialShareComponent {...socialPopoverProps}>
                  <SocialShare
                    title={safeProperty.title ?? ''}
                    url={propertyUrl}
                  />
                </SocialShareComponent>
                <Share />
              </ButtonContainer>
            )}
          </ButtonWrapper>
          {hasPrice && (
            <div className={cn('md:mt-px-32')}>
              {!!openContactForm ? (
                <div className={cn('flex flex-wrap my-px-32 md:my-0')}>
                  <SimpleMuiButton
                    variant="text"
                    className={cn('focus:outline-none cursor-pointer')}
                    onClick={() => {
                      setOpenContactForm(false)
                    }}
                  >
                    <HeadingH4 className={cn('pr-px-16 md:px-px-16')}>
                      {t('accentro:contactform.discardButton')}
                    </HeadingH4>
                    <CloseIcon />
                  </SimpleMuiButton>
                </div>
              ) : (
                <div>
                  <PropertyContactButton
                    onClick={() => {
                      setOpenContactForm(true)
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </ShareContactWrapper>
      </div>
      <div className={cn('col-span-12', !openContactForm && 'hidden')}>
        <PropAndProjectContactForm propId={property?.id} isOnDarkBg={false} />
      </div>
    </>
  )
}
